/* ============================================================
   tii OS — theme javascript
   zero dependencies / all sfx synthesized (no audio assets)
   nothing here may block the buying flow — every feature is
   progressive enhancement on top of plain working HTML.
   ============================================================ */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;

  /* ---------- tiny helpers ---------- */
  function $(sel, ctx) { return (ctx || doc).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }
  function store(key, val) {
    try {
      if (val === undefined) return localStorage.getItem(key);
      if (val === null) localStorage.removeItem(key); else localStorage.setItem(key, val);
    } catch (e) { return null; }
  }
  function buzz(ms) {
    if (store('tii:haptic') === '0') return;
    if (navigator.vibrate && matchMedia('(pointer: coarse)').matches) {
      try { navigator.vibrate(ms); } catch (e) {}
    }
  }

  /* ---------- shared loader glyphs (impl 2-F) ----------
     the hourglass: sand drains top→bottom, then the glass flips — drawn
     as tii's own clear-plastic timer (no console asset copied).
     the orbit: 8 dots relaying brightness around a circle — the
     "communicating…" street-pass memory. both are pure CSS animation. */
  var TII_HOUR = '<span class="tii-hour" aria-hidden="true"><svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M6 3.5 H18 M6 20.5 H18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" fill="none"/>' +
    '<path d="M7.5 4 C7.5 8.6 10.2 10.4 11 12 C10.2 13.6 7.5 15.4 7.5 20 M16.5 4 C16.5 8.6 13.8 10.4 13 12 C13.8 13.6 16.5 15.4 16.5 20" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/>' +
    '<polygon class="tii-hour__top" points="12,10.8 9.1,6 14.9,6" fill="currentColor"/>' +
    '<polygon class="tii-hour__bot" points="9.1,19 14.9,19 12,14.6" fill="currentColor"/>' +
    '<line class="tii-hour__str" x1="12" y1="12.4" x2="12" y2="18.2" stroke="currentColor" stroke-width="1.1" stroke-dasharray="1.2 1.6"/>' +
    '</svg></span>';
  function tiiOrbit() {
    var s = '';
    for (var k = 0; k < 8; k++) s += '<i style="--a:' + (k * 45) + 'deg;--d:' + (k * 0.1375).toFixed(4) + 's"></i>';
    return '<span class="tii-orbit" aria-hidden="true">' + s + '</span>';
  }

  /* ---------- sfx: synthesized handheld bleeps ----------
     two humanising tricks (borrowed from how the pros do it):
     - every play is pitch-shifted by a random ±6% so no two clicks
       sound identical (one fixed sample reads as "machine")
     - volume always ramps (~80ms release) — never hard-cuts, no pops */
  var SFX = (function () {
    var ctx = null;
    var muted = store('tii:muted') === '1';

    function ac() {
      if (!ctx) {
        var AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctx = new AC();
      }
      if (ctx.state === 'suspended') ctx.resume();
      return ctx;
    }
    /* one soft square-wave blip */
    function tone(freq, dur, delay, vol, type, drift) {
      var a = ac();
      if (!a || muted) return;
      var t = a.currentTime + (delay || 0);
      var osc = a.createOscillator();
      var gain = a.createGain();
      osc.type = type || 'square';
      osc.frequency.setValueAtTime(freq * (drift || 1), t);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(vol || 0.045, t + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + (dur || 0.06));
      gain.gain.linearRampToValueAtTime(0, t + (dur || 0.06) + 0.08);
      osc.connect(gain).connect(a.destination);
      osc.start(t);
      osc.stop(t + (dur || 0.06) + 0.1);
    }
    /* filtered-noise burst — the mechanical layer (slides, cassette seats).
       same synth-only rule: one shared noise buffer, always enveloped. */
    var noiseBuf = null;
    function noise(dur, delay, vol, f0, f1, type, q) {
      var a = ac();
      if (!a || muted) return;
      var t = a.currentTime + (delay || 0);
      if (!noiseBuf) {
        noiseBuf = a.createBuffer(1, Math.floor(a.sampleRate * 0.5), a.sampleRate);
        var ch = noiseBuf.getChannelData(0);
        for (var i = 0; i < ch.length; i++) ch[i] = Math.random() * 2 - 1;
      }
      var src = a.createBufferSource(); src.buffer = noiseBuf; src.loop = true;
      var fl = a.createBiquadFilter();
      fl.type = type || 'bandpass';
      fl.frequency.setValueAtTime(f0 || 1200, t);
      if (f1) fl.frequency.exponentialRampToValueAtTime(f1, t + dur);
      fl.Q.value = q || 1.1;
      var g = a.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(vol || 0.02, t + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      g.gain.linearRampToValueAtTime(0, t + dur + 0.06);
      src.connect(fl).connect(g).connect(a.destination);
      src.start(t); src.stop(t + dur + 0.08);
    }
    /* the buying staircase — tap, add, checkout each sit one rung higher
       (C5 → E5 → G5 → C6, plain-major handheld ladder) */
    var LADDER = [523.25, 659.25, 783.99, 1046.5, 1318.5];
    /* per-gesture random pitch: all notes of one gesture drift together */
    function humane(fn) {
      return function () {
        var drift = 0.94 + Math.random() * 0.12;
        fn(drift);
      };
    }
    return {
      unlock: function () { ac(); },
      muted: function () { return muted; },
      setMuted: function (m) { muted = m; store('tii:muted', m ? '1' : '0'); },
      tick:   humane(function (d) { tone(1750, 0.032, 0, 0.024, null, d); }),
      select: humane(function (d) { tone(660, 0.06, 0, null, null, d); tone(880, 0.07, 0.055, null, null, d); }),
      /* select.wav — the kit's bright "ピコン↑": C6→G6 perfect-fifth two-tone
         (kanta 7/21: this is the add-to-cart button sound). pure sine, faithful
         to sounds_original/select.wav (1046.5Hz 70ms + 1568Hz 120ms, 55ms gap) */
      selectwav: humane(function (d) { tone(1046.5, 0.07, 0, 0.05, 'sine', d); tone(1568, 0.12, 0.055, 0.05, 'sine', d); }),
      back:   humane(function (d) { tone(880, 0.05, 0, null, null, d); tone(587, 0.07, 0.045, null, null, d); }),
      equip:  humane(function (d) { tone(523, 0.07, 0, null, null, d); tone(659, 0.07, 0.07, null, null, d); tone(784, 0.1, 0.14, null, null, d); tone(1047, 0.16, 0.21, 0.05, null, d); }),
      boot:   function () { tone(523, 0.09, 0, 0.04, 'sine'); tone(784, 0.12, 0.1, 0.04, 'sine'); tone(1047, 0.3, 0.22, 0.045, 'sine'); },
      error:  humane(function (d) { tone(196, 0.12, 0, 0.04, null, d); tone(185, 0.16, 0.1, 0.04, null, d); }),
      secret: function () { [523, 587, 659, 784, 880, 1047].forEach(function (f, i) { tone(f, 0.08, i * 0.07); }); },
      blip: function (freq, vol, type) { tone(freq || 880, 0.05, 0, vol || 0.04, type || 'square'); },
      /* hover / focus micro-tick — barely-there, quieter than everything */
      hov: humane(function (d) { tone(1900, 0.02, 0, 0.009, null, d); }),
      /* page-slide "スッ" — a soft air swoosh (dir 1 = in/open, -1 = out/close) */
      swoosh: function (dir) {
        if (dir < 0) noise(0.22, 0, 0.013, 1500, 480, 'bandpass', 0.9);
        else noise(0.26, 0, 0.015, 520, 2100, 'bandpass', 0.9);
      },
      /* cassette insertion, upgraded: slide-in hiss → seat "ka" → low thunk →
         latch click → confirm blips one rung up the buying ladder */
      insert: function () {
        noise(0.12, 0, 0.014, 700, 2400, 'bandpass', 0.8);          /* slide-in */
        noise(0.03, 0.1, 0.026, 3200, null, 'bandpass', 2.2);       /* seat "ka" */
        tone(96, 0.09, 0.115, 0.05, 'sine');                        /* body thunk */
        noise(0.025, 0.17, 0.02, 1700, null, 'bandpass', 2.6);      /* latch */
        tone(LADDER[1], 0.06, 0.22, 0.032);                         /* confirm ♪ */
        tone(LADDER[2], 0.09, 0.28, 0.034);
      },
      /* two quick notes at a ladder rung — general staircase step */
      step: function (n) {
        n = Math.max(0, Math.min(LADDER.length - 2, n || 0));
        tone(LADDER[n], 0.045, 0, 0.03);
        tone(LADDER[n + 1], 0.06, 0.05, 0.03);
      },
      /* checkout — the top of the staircase (starts above the equip chime) */
      checkout: function () {
        tone(LADDER[2], 0.07, 0, 0.04);
        tone(LADDER[3], 0.08, 0.08, 0.042);
        tone(LADDER[4], 0.16, 0.16, 0.045);
        noise(0.2, 0.02, 0.01, 600, 2400, 'bandpass', 0.9);
      }
    };
  })();
  /* ambient menu pad — the quiet room-tone of the console menu. opt-in. */
  (function () {
    var ctx = null, amb = null;
    function ac() {
      if (!ctx) { var AC = window.AudioContext || window.webkitAudioContext; if (!AC) return null; ctx = new AC(); }
      if (ctx.state === 'suspended') ctx.resume();
      return ctx;
    }
    SFX.ambient = function (on) {
      if (SFX.muted() || store('tii:amb') !== '1') on = false;
      var c = ac(); if (!c) return;
      if (on && !amb) {
        var g = c.createGain(); g.gain.value = 0.006;
        var o1 = c.createOscillator(); o1.type = 'sine'; o1.frequency.value = 220;
        var o2 = c.createOscillator(); o2.type = 'sine'; o2.frequency.value = 330;
        var lfo = c.createOscillator(); lfo.frequency.value = 0.13;
        var lg = c.createGain(); lg.gain.value = 0.003;
        lfo.connect(lg).connect(g.gain);
        o1.connect(g); o2.connect(g); g.connect(c.destination);
        o1.start(); o2.start(); lfo.start();
        amb = { o1: o1, o2: o2, lfo: lfo };
      } else if (!on && amb) {
        try { amb.o1.stop(); amb.o2.stop(); amb.lfo.stop(); } catch (e) {}
        amb = null;
      }
    };
  })();
  window.tiiSFX = SFX;

  /* sound toggle button — text pill (status bar) or tii-toggle switch (footer) */
  function paintSoundBtn(btn) {
    var on = !SFX.muted();
    if (btn.classList.contains('tii-toggle')) { btn.setAttribute('aria-checked', String(on)); return; }
    btn.textContent = on ? 'söund: ön' : 'söund: öff';
    btn.setAttribute('aria-pressed', String(on));
  }
  $$('[data-tii-sound]').forEach(function (btn) {
    paintSoundBtn(btn);
    btn.addEventListener('click', function () {
      SFX.setMuted(!SFX.muted());
      SFX.unlock();
      if (!SFX.muted()) SFX.select();
      $$('[data-tii-sound]').forEach(paintSoundBtn);
    });
  });

  /* ---------- boot — warning screen → WHITEOUT → straight onto the board ----------
     kanta 7/14 (D1): keep the warning ritual, retire act 2 (logo + loading bar).
     press any button → the screen snaps to white → dissolves onto the channels. */
  (function () {
    var boot = $('[data-tii-boot]');
    if (!boot) return;
    var seen = store('tii:booted');
    var fresh = !seen || (Date.now() - Number(seen)) > 864e5; /* re-boot once a day */
    if (!fresh) { boot.remove(); return; }

    root.style.overflow = 'hidden';
    /* focus trap: keep keyboard/SR inside the boot dialog until it closes */
    try { boot.focus(); } catch (e) {}
    function trap(e) {
      if (e.key !== 'Tab' || !doc.contains(boot)) return;
      var f = boot.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
      if (!f.length) { e.preventDefault(); return; }
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    doc.addEventListener('keydown', trap);
    /* if today is the kid's saved birthday, the console celebrates */
    try {
      var kid = JSON.parse(localStorage.getItem('tii:kid') || 'null');
      var now = new Date();
      var mmdd = ('0' + (now.getMonth() + 1)).slice(-2) + '-' + ('0' + now.getDate()).slice(-2);
      if (kid && kid.bday === mmdd) {
        var hb = doc.createElement('p');
        hb.className = 'tii-boot__hb';
        hb.textContent = 'happÿ birthday' + (kid.nick ? ', ' + kid.nick : '') + ' ☆';
        var warn = $('.tii-boot__warn', boot);
        if (warn) warn.appendChild(hb);
      }
    } catch (e) {}

    var RM = matchMedia('(prefers-reduced-motion: reduce)').matches;
    var done = false;
    function enter(withSound) {
      if (done) return; done = true;
      clearTimeout(warnSafety); clearTimeout(hardT);
      store('tii:booted', String(Date.now()));
      SFX.unlock();
      if (withSound) { SFX.boot(); buzz(30); }
      var ds = $('[data-tii-ds]'); if (ds) ds.classList.add('is-wake');
      if (withSound && SFX.ambient) SFX.ambient(true);
      boot.classList.remove('is-act1');
      boot.classList.add('is-whiteout');           /* snap to white, dissolve */
      /* kanta 7/20: after the warning, the world is veiled in white mist for a
         beat, then the fog lifts and the shop is simply there — mystical, not a cut. */
      if (!RM) {
        var mist = doc.createElement('div');
        mist.className = 'tii-mist';
        mist.setAttribute('aria-hidden', 'true');
        doc.body.appendChild(mist);
        setTimeout(function () { mist.classList.add('is-clear'); }, 1050);
        setTimeout(function () { mist.remove(); }, 3400);
      }
      root.style.overflow = '';
      setTimeout(function () { boot.remove(); }, RM ? 80 : 1000);
      doc.removeEventListener('keydown', keys);
      doc.removeEventListener('keydown', trap);
      var main = doc.getElementById('tii-main');
      if (main) { main.setAttribute('tabindex', '-1'); try { main.focus(); } catch (e) {} }
    }
    function keys(e) {
      if (e.key === 'Tab') return;                 /* let the focus trap handle tab */
      if (e.key === 'Escape') { enter(false); return; }
      e.preventDefault(); enter(true);             /* any button = power on */
    }
    function tap(e) {
      if (e.target.closest('.tii-boot__skip')) return; /* skip has its own handler */
      e.preventDefault(); enter(true);
    }
    boot.addEventListener('click', tap);
    boot.addEventListener('touchend', tap, { passive: false });
    doc.addEventListener('keydown', keys);
    var skip = $('.tii-boot__skip', boot);
    if (skip) skip.addEventListener('click', function (e) { e.stopPropagation(); enter(false); });
    /* safety: auto-release — never make anyone wait at the door */
    var warnSafety = setTimeout(function () { enter(false); }, 8000);
    var hardT = setTimeout(function () { if (doc.contains(boot)) enter(false); }, 12000);
  })();

  /* ---------- star launch: tap a channel → a star pops toward you, then it opens ---------- */
  (function () {
    var tiles = $$('[data-tii-slot]');
    if (!tiles.length) return;
    var RM = matchMedia('(prefers-reduced-motion: reduce)').matches;
    var flash = doc.querySelector('.tii-launch-flash');
    if (!flash) { flash = doc.createElement('div'); flash.className = 'tii-launch-flash'; doc.body.appendChild(flash); }
    /* kanta D3: a ROUND, see-through star — soft tips (quad-bezier corners),
       skeleton-plastic fill (translucent white→water blue) and a clear edge
       that's brighter than the body, like light pooling in thick clear resin */
    var STAR = '<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">' +
      '<defs><linearGradient id="tiiStarG" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="#ffffff" stop-opacity=".95"/>' +
      '<stop offset=".55" stop-color="#E4F6FC" stop-opacity=".62"/>' +
      '<stop offset="1" stop-color="#BFE9F7" stop-opacity=".5"/></linearGradient></defs>' +
      '<path d="M46.2 14.2 Q50.0 6.0 53.8 14.2 L60.6 29.2 Q62.9 34.2 68.4 34.8 L84.8 36.7 Q93.7 37.8 87.1 43.9 L75.0 55.1 Q70.9 58.8 72.0 64.2 L75.3 80.4 Q77.0 89.2 69.2 84.8 L54.8 76.7 Q50.0 74.0 45.2 76.7 L30.8 84.8 Q23.0 89.2 24.7 80.4 L28.0 64.2 Q29.1 58.8 25.0 55.1 L12.9 43.9 Q6.3 37.8 15.2 36.7 L31.6 34.8 Q37.1 34.2 39.4 29.2 Z" ' +
      'fill="url(#tiiStarG)" stroke="rgba(125,216,240,.85)" stroke-width="3.2" stroke-linejoin="round"/>' +
      '<path d="M39 24 Q44 13 50 13" fill="none" stroke="rgba(255,255,255,.95)" stroke-width="3" stroke-linecap="round"/></svg>';
    var busy = false;
    function pop(x, y, href) {
      if (busy) return; busy = true;
      flash.style.setProperty('--fx', x + 'px');
      flash.style.setProperty('--fy', y + 'px');
      var star = doc.createElement('div');
      star.className = 'tii-star-pop';
      star.style.left = x + 'px'; star.style.top = y + 'px';
      star.innerHTML = STAR;
      doc.body.appendChild(star);
      SFX.unlock(); SFX.select(); SFX.equip(); buzz([12, 20, 40]);
      flash.classList.remove('is-on'); void flash.offsetWidth; flash.classList.add('is-on');
      requestAnimationFrame(function () { star.classList.add('is-go'); });
      setTimeout(function () { location.href = href; }, 500);
      /* safety: if navigation is blocked, let the tile work again */
      setTimeout(function () { busy = false; }, 1500);
    }
    tiles.forEach(function (tile) {
      tile.addEventListener('click', function (e) {
        var href = tile.getAttribute('href');
        /* let modifier / middle clicks open a new tab normally */
        if (!href || RM || e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        var r = tile.getBoundingClientRect();
        pop(r.left + r.width / 2, r.top + r.height / 2, href);
      });
    });
  })();

  /* ---------- status bar clock (blinking colon, like the wii clock) ---------- */
  (function () {
    var el = $('[data-tii-clock]');
    if (!el) return;
    function two(n) { return (n < 10 ? '0' : '') + n; }
    var DAYS = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'];
    function paint() {
      var d = new Date();
      var hm = two(d.getHours()) + '<span class="tii-colon">:</span>' + two(d.getMinutes());
      el.innerHTML = hm;
      var date = $('[data-tii-date]');
      if (date) date.textContent = two(d.getMonth() + 1) + '/' + two(d.getDate());
      var big = $('[data-tii-bigclock]');
      if (big) big.innerHTML = hm;
      var bigdate = $('[data-tii-bigdate]');
      if (bigdate) bigdate.textContent = DAYS[d.getDay()] + ' ' + two(d.getMonth() + 1) + '/' + two(d.getDate());
    }
    function schedule() {
      paint();
      var d = new Date();
      setTimeout(schedule, 60000 - (d.getSeconds() * 1000 + d.getMilliseconds()));
    }
    schedule();
  })();

  /* ---------- cart count ---------- */
  function paintCartCount(n) {
    $$('[data-tii-bag-count]').forEach(function (el) {
      if (el.hasAttribute('data-tii-bag-count') && window.tiiBagOdo && el === document.querySelector('[data-tii-bag-count]')) {
        el.style.visibility = n > 0 ? 'visible' : 'hidden';
        window.tiiBagOdo(n);
      } else {
        el.textContent = n;
        el.style.visibility = n > 0 ? 'visible' : 'hidden';
      }
    });
  }
  function refreshCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { paintCartCount(cart.item_count || 0); renderDrawer(cart); return cart; })
      .catch(function () {});
  }

  /* ---------- item-bag drawer: cart without leaving the page ---------- */
  var drawer = $('[data-tii-drawer]');
  function money(cents) {
    var n = Math.round(cents) / 100;
    return '¥' + n.toLocaleString('ja-JP');
  }
  function renderDrawer(cart) {
    if (!drawer) return;
    var body = $('[data-tii-drawer-body]', drawer);
    var foot = $('[data-tii-drawer-foot]', drawer);
    var count = $('[data-tii-drawer-count]', drawer);
    var empty = $('[data-tii-drawer-empty]', drawer);
    if (count) count.textContent = (cart.item_count || 0) + ' slot' + (cart.item_count === 1 ? '' : 's');
    if (!cart.items || !cart.items.length) {
      if (empty) empty.hidden = false;
      $$('.tii-drawer__row', drawer).forEach(function (r) { r.remove(); });
      if (foot) foot.hidden = true;
      return;
    }
    if (empty) empty.hidden = true;
    $$('.tii-drawer__row', drawer).forEach(function (r) { r.remove(); });
    cart.items.forEach(function (it, i) {
      var row = doc.createElement('div');
      row.className = 'tii-drawer__row';
      var img = it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') : '';
      row.innerHTML =
        '<span class="tii-drawer__thumb">' + (img ? '<img src="' + img + '" alt="" width="60" height="60" loading="lazy">' : '') + '</span>' +
        '<span class="tii-drawer__info"><a class="tii-drawer__name" href="' + it.url + '">' + it.product_title + '</a>' +
        (it.variant_title ? '<span class="tii-drawer__variant">' + it.variant_title + '</span>' : '') +
        '<span class="tii-drawer__qty"><button type="button" class="tii-stepper__btn" data-tii-qty="-1" data-line="' + (i + 1) + '" aria-label="minus">−</button><b>' + it.quantity + '</b><button type="button" class="tii-stepper__btn" data-tii-qty="1" data-line="' + (i + 1) + '" aria-label="plus">+</button></span></span>' +
        '<span class="tii-drawer__price">' + money(it.final_line_price) + '</span>';
      body.appendChild(row);
    });
    if (foot) {
      foot.hidden = false;
      var tot = $('[data-tii-drawer-total]', drawer);
      if (tot) tot.textContent = money(cart.total_price);
    }
    /* free-shipping progress */
    var ship = $('[data-tii-ship]', drawer);
    if (ship) {
      var thr = Number(ship.getAttribute('data-threshold')) * 100;
      var left = thr - cart.total_price;
      var txt = $('[data-tii-ship-txt]', drawer), bar = $('[data-tii-ship-bar]', drawer);
      /* kanta 7/31: ストアは英語のみ（locale=en 単独）なのに、ここだけ日本語だった
         （旧: 'あと ¥X で sö送料 free ☆'）。配送方法名の日本語を英語に直したのと同じ直し。
         ★日本語に戻したいならこの2行だけ差し替えれば元通り。 */
      if (left > 0) { if (txt) txt.textContent = money(left) + ' möre tö free shipping ☆'; }
      else { if (txt) txt.textContent = 'free shipping unlöcked ☆'; }
      if (bar) bar.style.width = Math.max(0, Math.min(100, cart.total_price / thr * 100)) + '%';
    }
  }
  function openDrawer() {
    if (!drawer) return;
    drawer.hidden = false;
    requestAnimationFrame(function () { drawer.classList.add('is-open'); });
    root.style.overflow = 'hidden';
    SFX.select(); SFX.swoosh(1); /* the bag slides in */
    var p = $('.tii-drawer__panel', drawer); if (p) try { p.focus(); } catch (e) {}
  }
  window.tiiOpenDrawer = function () { refreshCart(); openDrawer(); };
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    root.style.overflow = '';
    SFX.back(); SFX.swoosh(-1); /* …and slides back out */
    setTimeout(function () { drawer.hidden = true; }, 320);
  }
  if (drawer) {
    /* open from any bag link (intercept navigation to /cart) */
    doc.addEventListener('click', function (e) {
      var bag = e.target.closest && e.target.closest('.tii-status__bag, .tii-tray__btn--right, [data-tii-open-bag]');
      if (bag && !e.metaKey && !e.ctrlKey) { e.preventDefault(); refreshCart(); openDrawer(); }
    });
    /* tii-stepper (impl 2-E): tap = ±1, HOLD = the counter spins with a tick
       per step (handheld volume-button memory). the network commit happens
       once, on release — the display leads, the cart follows. */
    function commitQty(line, qty) {
      fetch('/cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Math.max(0, qty) })
      }).then(function (r) { return r.json(); }).then(function (cart) { paintCartCount(cart.item_count || 0); renderDrawer(cart); }).catch(function () {});
    }
    var holdWait = null, holdRun = null, holdWas = false;
    drawer.addEventListener('pointerdown', function (e) {
      var qb = e.target.closest('[data-tii-qty]');
      if (!qb) return;
      var b = qb.parentElement.querySelector('b');
      var delta = Number(qb.getAttribute('data-tii-qty'));
      holdWas = false;
      holdWait = setTimeout(function () {
        holdWas = true;
        holdRun = setInterval(function () {
          var next = Math.max(0, Number(b.textContent) + delta);
          if (next === Number(b.textContent)) return;
          b.textContent = next;
          SFX.tick(); buzz(6);
        }, 130);
      }, 420);
      var release = function () {
        clearTimeout(holdWait); holdWait = null;
        if (holdRun) {
          clearInterval(holdRun); holdRun = null;
          commitQty(qb.getAttribute('data-line'), Number(b.textContent));
        }
        doc.removeEventListener('pointerup', release);
        doc.removeEventListener('pointercancel', release);
      };
      doc.addEventListener('pointerup', release);
      doc.addEventListener('pointercancel', release);
    });
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-tii-drawer-close]')) { closeDrawer(); return; }
      var qb = e.target.closest('[data-tii-qty]');
      if (qb) {
        if (holdWas) { holdWas = false; return; } /* the hold already committed */
        var line = qb.getAttribute('data-line');
        var delta = Number(qb.getAttribute('data-tii-qty'));
        var cur = Number(qb.parentElement.querySelector('b').textContent);
        SFX.tick(); buzz(8);
        commitQty(line, cur + delta);
      }
    });
    doc.addEventListener('keydown', function (e) { if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer(); });
  }
  refreshCart();

  /* ---------- toast ---------- */
  var toast = (function () {
    var el = doc.createElement('div');
    el.className = 'tii-toast';
    el.setAttribute('role', 'status');
    doc.body.appendChild(el);
    var timer;
    return function (msg) {
      el.textContent = msg;
      el.classList.add('is-on');
      clearTimeout(timer);
      timer = setTimeout(function () { el.classList.remove('is-on'); }, 2600);
    };
  })();

  /* ---------- product form: slots + ajax equip ---------- */
  $$('[data-tii-product]').forEach(function (wrap) {
    var dataEl = $('[data-tii-variants]', wrap);
    var form = $('form[data-tii-form]', wrap);
    if (!dataEl || !form) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var idInput = $('input[name="id"]', form);
    var button = $('[data-tii-equip]', form);
    var priceEl = $('[data-tii-price]', wrap);

    function currentOptions() {
      return $$('.tii-slots', wrap).map(function (group) {
        var active = $('.tii-slot.is-active', group);
        return active ? active.getAttribute('data-value') : null;
      });
    }
    function matchVariant() {
      var opts = currentOptions();
      /* default-title products render no option slots — the single variant IS
         the match (without this, the buy button painted "unavailable" and a
         one-variant product could never be bought through the styled button) */
      if (!opts.length) return variants[0];
      return variants.filter(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      })[0];
    }
    var label = $('[data-tii-equip-label]', form) || button;
    /* low-stock HP gauge (impl 1-B): "▓▓░░ 3 left" — remaining-lives style.
       shows only when the selected variant is tracked, in stock, and at or
       below the threshold. blinks on the last one. */
    var stock = $('[data-tii-stock]', wrap);
    function paintStock(v) {
      if (!stock) return;
      var thr = Number(stock.getAttribute('data-threshold')) || 5;
      var show = !!(v && v.available && v.tracked && v.qty > 0 && v.qty <= thr);
      stock.hidden = !show;
      stock.classList.toggle('is-critical', show && v.qty === 1);
      if (!show) return;
      var cells = $('[data-tii-stock-cells]', stock);
      var lab = $('[data-tii-stock-label]', stock);
      var html = '';
      for (var i = 0; i < thr; i++) html += i < v.qty ? '<i class="is-on"></i>' : '<i></i>';
      if (cells) cells.innerHTML = html;
      if (lab) lab.textContent = v.qty + ' left';
    }
    function paint() {
      var v = matchVariant();
      paintStock(v);
      if (!v) {
        button.disabled = true;
        label.textContent = button.getAttribute('data-label-unavailable');
        return;
      }
      idInput.value = v.id;
      if (priceEl && v.price_formatted) priceEl.textContent = v.price_formatted;
      if (v.available) {
        button.disabled = false;
        label.textContent = button.getAttribute('data-label-equip');
      } else {
        button.disabled = true;
        label.textContent = button.getAttribute('data-label-soldout');
      }
    }
    $$('.tii-slot', wrap).forEach(function (slot) {
      slot.addEventListener('click', function (e) {
        e.preventDefault();
        if (slot.classList.contains('is-disabled')) { SFX.error(); return; }
        $$('.tii-slot', slot.parentElement).forEach(function (s) {
          s.classList.remove('is-active');
          if (s.hasAttribute('aria-checked')) s.setAttribute('aria-checked', 'false');
        });
        slot.classList.add('is-active');
        if (slot.hasAttribute('aria-checked')) slot.setAttribute('aria-checked', 'true');
        SFX.tick(); buzz(10);
        paint();
      });
      /* radiogroup keyboard: arrows move + select the neighbour slot */
      slot.addEventListener('keydown', function (e) {
        if (slot.getAttribute('role') !== 'radio') return;
        var group = $$('.tii-slot', slot.parentElement);
        var i = group.indexOf(slot), n = i;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % group.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + group.length) % group.length;
        else return;
        e.preventDefault(); group[n].focus(); group[n].click();
      });
    });
    paint();

    form.addEventListener('submit', function (e) {
      /* ajax equip — falls back to normal POST if fetch fails */
      if (!window.fetch) return;
      e.preventDefault();
      if (button.disabled) return;
      button.disabled = true;
      button.classList.add('is-loading');
      label.textContent = 'ädding…';
      SFX.tick();
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(idInput.value), quantity: 1 })
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      }).then(function () {
        buzz([20, 40, 20]);
        button.classList.remove('is-loading');
        button.classList.add('is-added');
        label.textContent = button.getAttribute('data-label-equipped');
        var pt = wrap.querySelector('[data-tii-cart-anim]');
        if (pt) { pt.classList.remove('is-in'); void pt.offsetWidth; pt.classList.add('is-in'); }
        SFX.selectwav(); /* cart-add confirm = kit select.wav ピコン↑ (kanta 7/21) */
        var nm = (wrap.querySelector('.tii-itemscreen__title') || {}).textContent || 'new item';
        /* the item itself flies into the bag — game pickup, ACO-parity (kanta 7/20) */
        var flew = false;
        try {
          var gimg = doc.querySelector('.tii-gallery__main img');
          var bagBtn = doc.querySelector('.tii-status__bag');
          if (gimg && bagBtn && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
            var r0 = gimg.getBoundingClientRect(), r1 = bagBtn.getBoundingClientRect();
            if (r0.width > 10 && r1.width > 4) {
              var fly = doc.createElement('img');
              fly.src = gimg.currentSrc || gimg.src;
              fly.className = 'tii-fly';
              fly.style.left = r0.left + 'px'; fly.style.top = r0.top + 'px';
              fly.style.width = r0.width + 'px'; fly.style.height = r0.height + 'px';
              doc.body.appendChild(fly);
              var dx = (r1.left + r1.width / 2) - (r0.left + r0.width / 2);
              var dy = (r1.top + r1.height / 2) - (r0.top + r0.height / 2);
              requestAnimationFrame(function () {
                fly.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(0.05)';
                fly.style.opacity = '0.3';
              });
              flew = true;
              setTimeout(function () { fly.remove(); if (window.tiiBagPop) window.tiiBagPop(); }, 640);
            }
          }
        } catch (e) {}
        if (!flew && window.tiiBagPop) window.tiiBagPop();
        if (window.tiiAchievement) window.tiiAchievement('NEW ITEM UNLOCKED ☆', nm + ' :: added to bag'); else { SFX.equip(); toast('item equipped'); }
        refreshCart();
        /* slide the bag open a beat after the pop so they can review / checkout */
        setTimeout(function () { if (window.tiiOpenDrawer) window.tiiOpenDrawer(); }, 900);
        setTimeout(function () {
          button.classList.remove('is-added');
          button.disabled = false;
          paint();
        }, 1800);
      }).catch(function () {
        button.classList.remove('is-loading');
        button.disabled = false;
        form.submit(); /* graceful fallback: server-side add */
      });
    });
  });

  /* ---------- product gallery: thumbs + 1P/2P/3P/4P player LEDs (kanta D4) ----------
     the LED row shows which photo is on screen, controller-style. thumbs, LEDs
     and a swipe on the photo all drive the same show() — everything stays lit
     in sync. swipe wraps around like flicking pages on a handheld. */
  $$('[data-tii-gallery]').forEach(function (gal) {
    var main = $('.tii-gallery__main img', gal);
    var thumbs = $$('.tii-gallery__thumb', gal);
    var leds = $$('.tii-player', gal);
    if (!main || !thumbs.length) return;
    var cur = 0;
    function show(i, silent) {
      i = ((i % thumbs.length) + thumbs.length) % thumbs.length; /* wrap */
      var t = thumbs[i];
      var src = t.getAttribute('data-full');
      if (src) {
        /* kanta 7/30: a real tap wins over the pending spin swap (see spin swap below) */
        if (!silent) main.removeAttribute('data-tii-spin');
        main.src = src;
        if (t.getAttribute('data-alt')) main.alt = t.getAttribute('data-alt');
      }
      thumbs.forEach(function (x, j) { x.classList.toggle('is-active', j === i); });
      leds.forEach(function (x, j) {
        x.classList.toggle('is-lit', j === i);
        x.setAttribute('aria-selected', j === i ? 'true' : 'false');
      });
      cur = i;
      if (!silent) { SFX.tick(); buzz(8); }
    }
    thumbs.forEach(function (thumb, i) { thumb.addEventListener('click', function () { show(i); }); });
    leds.forEach(function (led, i) { led.addEventListener('click', function () { show(i); }); });
    /* Wii side chevrons — same page-flip on phone and desktop (kanta 7/20) */
    var prevB = $('[data-tii-gal-prev]', gal), nextB = $('[data-tii-gal-next]', gal);
    if (prevB) prevB.addEventListener('click', function () { show(cur - 1); });
    if (nextB) nextB.addEventListener('click', function () { show(cur + 1); });
    /* swipe the big photo left / right */
    var stage = $('.tii-gallery__main', gal);
    if (stage && thumbs.length > 1) {
      var x0 = null, y0 = null;
      stage.addEventListener('touchstart', function (e) {
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        x0 = null;
        if (Math.abs(dx) < 42 || Math.abs(dx) < Math.abs(dy) * 1.4) return; /* a scroll, not a flick */
        show(cur + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }
  });

  /* first real touch primes the audio context (iOS needs a gesture) */
  doc.addEventListener('touchend', function prime() {
    SFX.unlock();
    doc.removeEventListener('touchend', prime);
  }, { passive: true });

  /* ---------- bleep on interactive hover / nav select ---------- */
  var hoverables = '.tii-channel, .tii-item, .tii-cart-row, .tii-status__btn, .tii-equip, .tii-nav a, .tii-stage, .tii-ch__link, .tii-shortcut, .tii-tray__btn, .tii-slot, .tii-gallery__thumb';
  doc.addEventListener('mouseover', function (e) {
    var t = e.target.closest && e.target.closest(hoverables);
    if (t && !t._tiiHov) {
      t._tiiHov = true;
      SFX.hov(); /* micro-tick — quieter than every other voice */
      setTimeout(function () { t._tiiHov = false; }, 220);
    }
  });
  /* keyboard focus gets the same micro-tick (focus-visible only — no sound
     when a click already blipped and left focus behind) */
  doc.addEventListener('focusin', function (e) {
    var t = e.target;
    if (!t || !t.matches || t._tiiHov) return;
    if (!t.matches(hoverables + ', .tii-equip, input, select, textarea, [data-tii-look-all]')) return;
    var fv = false;
    try { fv = t.matches(':focus-visible'); } catch (err) { fv = true; }
    if (!fv) return;
    t._tiiHov = true;
    SFX.hov();
    setTimeout(function () { t._tiiHov = false; }, 220);
  });

  /* ---------- wii hover label: a little bubble pops over the channel ---------- */
  (function () {
    if (matchMedia('(pointer: coarse)').matches) return; /* desktop pointer only */
    var bubble = doc.createElement('div');
    bubble.className = 'tii-bubble';
    bubble.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(bubble);
    doc.addEventListener('mouseover', function (e) {
      var tile = e.target.closest && e.target.closest('.tii-channel:not(.tii-channel--empty), .tii-stage:not(.is-locked)');
      if (!tile) return;
      var nameEl = tile.querySelector('.tii-channel__name, .tii-stage__name');
      if (!nameEl) return;
      bubble.textContent = nameEl.textContent.trim();
      var r = tile.getBoundingClientRect();
      bubble.style.left = (r.left + r.width / 2) + 'px';
      bubble.style.top = (r.top + window.scrollY - 10) + 'px';
      bubble.classList.add('is-on');
    });
    doc.addEventListener('mouseout', function (e) {
      if (e.target.closest && e.target.closest('.tii-channel, .tii-stage')) bubble.classList.remove('is-on');
    });
    doc.addEventListener('scroll', function () { bubble.classList.remove('is-on'); }, { passive: true });
  })();
  doc.addEventListener('click', function (e) {
    /* click blip for links/buttons that navigate without their own SFX.
       (excludes .tii-ch__link — it fires the star launch — and .tii-equip/.tii-slot) */
    var t = e.target.closest && e.target.closest('.tii-item a, .tii-cart-row, .tii-shortcut, .tii-tray__btn, .tii-stage a, .tii-footer__col a, .tii-pager a, .tii-nav a, .tii-status__bag, .tii-bag__qty-btn, .tii-bag__remove, .tii-dock__btn, .tii-wii__all');
    if (t) {
      var a = t.tagName === 'A' ? t : (t.closest && t.closest('a'));
      var path = a ? (a.getAttribute('href') || '').replace(/[?#].*$/, '') : '';
      if (path === '/') { SFX.back(); } /* going home = the wii "back" chime */
      else { SFX.select(); }
      buzz(10);
    }
  });

  /* ---------- your cölör: the DS favorite-colour, water palette only ---------- */
  (function () {
    var PAL = {
      sky:      { blue: '#7DD8F0', soft: '#B8E8F5', pale: '#DFF4FB', deep: '#4FB8D8', clear: '174, 226, 247', edge: 'rgba(79, 184, 216, 0.45)' },
      mint:     { blue: '#8FE3C9', soft: '#C4F0E2', pale: '#E5F8F1', deep: '#5BC4A4', clear: '196, 240, 226', edge: 'rgba(91, 196, 164, 0.45)' },
      lavender: { blue: '#C7C4F2', soft: '#DEDCF8', pale: '#EFEEFB', deep: '#9B96E0', clear: '222, 220, 248', edge: 'rgba(155, 150, 224, 0.45)' },
      peach:    { blue: '#F7CDB9', soft: '#FBE2D6', pale: '#FDF1EA', deep: '#E8A588', clear: '251, 226, 214', edge: 'rgba(232, 165, 136, 0.45)' },
      milk:     { blue: '#D7E2E9', soft: '#E9EDF0', pale: '#F4F6F8', deep: '#A9BEC9', clear: '233, 237, 240', edge: 'rgba(169, 190, 201, 0.45)' }
    };
    function apply(key) {
      var p = PAL[key];
      if (!p) return;
      root.style.setProperty('--tii-blue', p.blue);
      root.style.setProperty('--tii-blue-soft', p.soft);
      root.style.setProperty('--tii-blue-pale', p.pale);
      root.style.setProperty('--tii-blue-deep', p.deep);
      root.style.setProperty('--tii-clear', p.clear);
      root.style.setProperty('--tii-clear-edge', p.edge);
      $$('[data-tii-colour]').forEach(function (b) {
        b.classList.toggle('is-active', b.getAttribute('data-tii-colour') === key);
      });
    }
    var saved = store('tii:colour');
    if (saved && PAL[saved]) apply(saved);
    $$('[data-tii-colour]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-tii-colour');
        apply(key);
        store('tii:colour', key);
        SFX.select(); buzz(10);
        toast('cölör saved :: ' + key + ' ☆');
      });
    });
  })();

  /* ---------- büzz: haptics get their own switch, like the real thing ---------- */
  (function () {
    function paintBuzz(btn) {
      var on = store('tii:haptic') !== '0';
      if (btn.classList.contains('tii-toggle')) { btn.setAttribute('aria-checked', String(on)); return; }
      btn.textContent = on ? 'büzz: ön' : 'büzz: öff';
      btn.setAttribute('aria-pressed', String(on));
    }
    $$('[data-tii-haptic]').forEach(function (btn) {
      paintBuzz(btn);
      btn.addEventListener('click', function () {
        store('tii:haptic', store('tii:haptic') === '0' ? '1' : '0');
        $$('[data-tii-haptic]').forEach(paintBuzz);
        if (store('tii:haptic') !== '0') buzz(20);
        SFX.tick();
      });
    });
    function paintAmb(btn) {
      var on = store('tii:amb') === '1';
      if (btn.classList.contains('tii-toggle')) { btn.setAttribute('aria-checked', String(on)); return; }
      btn.textContent = on ? 'ämbient: ön' : 'ämbient: öff';
      btn.setAttribute('aria-pressed', String(on));
    }
    $$('[data-tii-amb]').forEach(function (btn) {
      paintAmb(btn);
      btn.addEventListener('click', function () {
        store('tii:amb', store('tii:amb') === '1' ? '0' : '1');
        $$('[data-tii-amb]').forEach(paintAmb);
        if (SFX.ambient) SFX.ambient(store('tii:amb') === '1');
        SFX.tick();
      });
    });
  })();

  /* ---------- drop calendar ---------- */
  $$('[data-tii-calendar]').forEach(function (cal) {
    var dropDates = [];
    try { dropDates = JSON.parse(cal.getAttribute('data-drops') || '[]'); } catch (e) {}
    var now = new Date();
    var y = now.getFullYear(), m = now.getMonth();
    var first = new Date(y, m, 1).getDay();
    var days = new Date(y, m + 1, 0).getDate();
    var drops = dropDates.map(function (s) {
      var p = String(s).split('-');
      return (Number(p[0]) === y && Number(p[1]) === m + 1) ? Number(p[2]) : null;
    });
    var html = '<table><thead><tr>';
    ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'].forEach(function (d) { html += '<th>' + d + '</th>'; });
    html += '</tr></thead><tbody><tr>';
    var cell = 0;
    for (var i = 0; i < first; i++) { html += '<td></td>'; cell++; }
    for (var d = 1; d <= days; d++) {
      if (cell % 7 === 0 && cell !== 0) html += '</tr><tr>';
      var cls = [];
      if (d === now.getDate()) cls.push('is-today');
      if (drops.indexOf(d) !== -1) cls.push('is-drop');
      html += '<td' + (cls.length ? ' class="' + cls.join(' ') + '"' : '') + '>' + d + '</td>';
      cell++;
    }
    html += '</tr></tbody></table>';
    cal.innerHTML = html;
    var head = $('[data-tii-calendar-month]');
    if (head) head.textContent = String(m + 1).padStart(2, '0') + '/' + y;
  });

  /* ---------- secret command: ↑↑↓↓←→←→ b a ---------- */
  (function () {
    var target = doc.body.getAttribute('data-tii-secret-url');
    if (!target) return;
    var code = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
    var pos = 0;
    doc.addEventListener('keydown', function (e) {
      if (e.target.matches('input, textarea, select')) return;
      pos = (e.key === code[pos]) ? pos + 1 : (e.key === code[0] ? 1 : 0);
      if (pos === code.length) {
        pos = 0;
        SFX.secret(); buzz([30, 30, 30, 30, 60]);
        toast('ura command accepted ☆');
        setTimeout(function () { location.href = target; }, 900);
      }
    });
    /* mobile: tap the logo 8 times */
    var logo = $('.tii-status__logo');
    var taps = 0, tapTimer;
    if (logo) logo.addEventListener('click', function (e) {
      taps++;
      clearTimeout(tapTimer);
      tapTimer = setTimeout(function () { taps = 0; }, 2500);
      if (taps >= 8) {
        e.preventDefault();
        taps = 0;
        SFX.secret(); buzz([30, 30, 30, 30, 60]);
        toast('ura command accepted ☆');
        setTimeout(function () { location.href = target; }, 900);
      }
    });
  })();

  /* ---------- change console. (skin toggle) ---------- */
  (function () {
    var saved = store('tii:console');
    if (saved) root.setAttribute('data-console', saved);
    $$('[data-tii-console]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var next = root.getAttribute('data-console') === 'dsi' ? 'ds' : 'dsi';
        root.setAttribute('data-console', next);
        store('tii:console', next);
        SFX.back();
        toast('console changed :: ' + next + ' mode');
      });
    });
  })();

  /* ---------- soft page wipe on internal nav ----------
     skipped when the browser supports cross-document view transitions —
     there the wii channel-zoom takes over and a wipe would fight it. */
  (function () {
    if (window.CSS && CSS.supports && CSS.supports('view-transition-name: a') && doc.startViewTransition) return;
    var wipe = doc.createElement('div');
    wipe.className = 'tii-wipe';
    doc.body.appendChild(wipe);
    doc.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a || a.target === '_blank' || e.metaKey || e.ctrlKey) return;
      var href = a.getAttribute('href') || '';
      if (href.indexOf('/') !== 0 || href.indexOf('#') === 0) return;
      wipe.classList.add('is-on'); /* fades out naturally on next page load */
    });
    window.addEventListener('pageshow', function () { wipe.classList.remove('is-on'); });
  })();

  /* ---------- now-loading pill: the DS "すれちがい通信" texture on every nav ---------- */
  (function () {
    var load = doc.createElement('div');
    load.className = 'tii-nowload'; load.setAttribute('aria-hidden', 'true');
    load.innerHTML = TII_HOUR + '<span class="tii-nowload__txt">now löading</span>' + tiiOrbit();
    doc.body.appendChild(load);
    doc.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a || a.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      var href = a.getAttribute('href') || '';
      if (href.indexOf('/') !== 0 || href.indexOf('#') === 0) return;
      load.classList.add('is-on');
      SFX.swoosh(1); /* the page slides away — "スッ" under the click blip */
    }, true);
    window.addEventListener('pageshow', function () { load.classList.remove('is-on'); });
  })();

  /* ---------- product recommendations: fetch + reveal "recömmended für yöu" ---------- */
  (function () {
    var host = $('[data-tii-recs]');
    if (!host || !host.getAttribute('data-url')) return;
    fetch(host.getAttribute('data-url'))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var tmp = doc.createElement('div');
        tmp.innerHTML = html;
        var sec = tmp.querySelector('.tii-recs');
        if (!sec) return;
        host.appendChild(sec);
        requestAnimationFrame(function () { sec.classList.add('is-in'); });
        /* the new cards want the loaded-image shimmer too */
        $$('.tii-item__media img', sec).forEach(function (img) {
          function done() { img.parentElement.classList.add('is-loaded'); }
          if (img.complete) done(); else { img.addEventListener('load', done); img.addEventListener('error', done); }
        });
      })
      .catch(function () {});
  })();

  /* ---------- recently viewed: "recently played" (localStorage) ---------- */
  (function () {
    var curEl = $('[data-tii-seen-current]');
    var strip = $('[data-tii-seen]');
    var KEY = 'tii:seen';
    var list = [];
    try { list = JSON.parse(store(KEY) || '[]'); } catch (e) { list = []; }
    var cur = null;
    if (curEl) { try { cur = JSON.parse(curEl.textContent); } catch (e) {} }
    if (cur && cur.handle) {
      list = list.filter(function (it) { return it.handle !== cur.handle; });
      list.unshift(cur);
      list = list.slice(0, 8);
      store(KEY, JSON.stringify(list));
    }
    if (!strip) return;
    var grid = $('[data-tii-seen-grid]', strip);
    var others = list.filter(function (it) { return !cur || it.handle !== cur.handle; });
    if (!others.length || !grid) return;
    others.slice(0, 4).forEach(function (it) {
      var li = doc.createElement('li'); li.className = 'tii-seen__cell';
      li.innerHTML = '<a href="' + it.url + '">' +
        '<span class="tii-seen__media">' + (it.image ? '<img src="' + it.image + '" alt="" loading="lazy">' : '') + (it.available === false ? '<span class="tii-seen__flag">söld öut</span>' : '') + '</span>' +
        '<span class="tii-seen__name">' + it.title + '</span>' +
        '<span class="tii-seen__price">' + (it.price || '') + '</span></a>';
      grid.appendChild(li);
    });
    strip.hidden = false;
  })();

  /* ---------- predictive search: live suggestions from /search/suggest.json ---------- */
  $$('[data-tii-predictive]').forEach(function (form) {
    var input = $('[data-tii-predictive-input]', form);
    var box = $('[data-tii-suggest]', form);
    if (!input || !box) return;
    var t, lastQ = '';
    function money(cents) { return '¥' + (Math.round(cents) / 100).toLocaleString('ja-JP'); }
    function hide() { box.hidden = true; box.innerHTML = ''; }
    function run() {
      var q = input.value.trim();
      if (q.length < 2) { hide(); return; }
      if (q === lastQ) return; lastQ = q;
      box.hidden = false;
      box.innerHTML = '<div class="tii-suggest__load">' + tiiOrbit() + '<span>searching… ♪</span></div>';
      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product,page,article&resources[limit]=6&resources[options][unavailable_products]=last';
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (input.value.trim() !== q) return;
          var res = (d.resources && d.resources.results) || {};
          var prods = res.products || [], pages = (res.pages || []).concat(res.articles || []);
          if (!prods.length && !pages.length) { box.innerHTML = '<div class="tii-suggest__none">no items för "' + q + '"</div>'; return; }
          var html = '';
          prods.forEach(function (p) {
            var img = (p.featured_image && p.featured_image.url) ? p.featured_image.url.replace(/(\.[a-z]+)(\?|$)/i, '_80x$1$2') : '';
            var price = p.price ? money(p.price) : (p.price_min ? money(p.price_min) : '');
            html += '<a class="tii-suggest__row" href="' + p.url + '">' +
              '<span class="tii-suggest__thumb">' + (img ? '<img src="' + img + '" alt="" width="40" height="40" loading="lazy">' : '') + '</span>' +
              '<span class="tii-suggest__name">' + p.title + '</span>' +
              '<span class="tii-suggest__price">' + price + '</span></a>';
          });
          pages.forEach(function (pg) {
            html += '<a class="tii-suggest__row tii-suggest__row--page" href="' + pg.url + '"><span class="tii-suggest__thumb">▸</span><span class="tii-suggest__name">' + pg.title + '</span></a>';
          });
          box.innerHTML = html;
          SFX.tick();
        })
        .catch(function () { hide(); });
    }
    input.addEventListener('input', function () { clearTimeout(t); t = setTimeout(run, 180); });
    input.addEventListener('focus', function () { if (box.innerHTML) box.hidden = false; });
    doc.addEventListener('click', function (e) { if (!form.contains(e.target)) hide(); });
    input.addEventListener('keydown', function (e) { if (e.key === 'Escape') hide(); });
  });

  /* ---------- collection facets: auto-submit filter/sort (no JS = manual apply) ---------- */
  $$('[data-tii-facets]').forEach(function (form) {
    var t;
    form.addEventListener('change', function (e) {
      if (!e.target.matches('[data-tii-autosubmit]')) return;
      SFX.tick(); buzz(6);
      /* the tii-check tick draws itself in right away, before the reload */
      var chip = e.target.closest('.tii-facet-chip');
      if (chip) chip.classList.toggle('is-on', e.target.checked);
      clearTimeout(t);
      t = setTimeout(function () {
        /* strip empty fields so the URL stays clean */
        var fd = new FormData(form), qs = new URLSearchParams();
        fd.forEach(function (v, k) { if (v !== '' && v != null) qs.append(k, v); });
        var base = location.pathname;
        location.href = base + (qs.toString() ? '?' + qs.toString() : '');
      }, e.target.type === 'number' ? 700 : 60);
    });
  });

  /* ---------- drop trailer: click-to-load embed (home stays instant) ---------- */
  $$('[data-tii-trailer]').forEach(function (frame) {
    var btn = $('.tii-trailer__play', frame);
    if (!btn) return;
    btn.addEventListener('click', function () {
      var type = frame.getAttribute('data-video-type');
      var id = frame.getAttribute('data-video-id');
      if (!id) return;
      var src = type === 'vimeo'
        ? 'https://player.vimeo.com/video/' + id + '?autoplay=1'
        : 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0';
      var ifr = doc.createElement('iframe');
      ifr.src = src;
      ifr.allow = 'autoplay; encrypted-media; picture-in-picture';
      ifr.allowFullscreen = true;
      ifr.title = 'drop trailer';
      frame.appendChild(ifr);
      btn.remove();
      SFX.select();
    });
  });

  /* ---------- top-screen parallax: the depth follows the mouse ---------- */
  if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
    doc.addEventListener('mousemove', function (e) {
      root.style.setProperty('--px', (e.clientX / innerWidth - 0.5).toFixed(3));
      root.style.setProperty('--py', (e.clientY / innerHeight - 0.5).toFixed(3));
    }, { passive: true });
  }

  /* ---------- announce strip: marquee only when the text overflows ---------- */
  $$('.tii-announce').forEach(function (pill) {
    var txt = $('.tii-announce__txt', pill);
    if (!txt) return;
    function check() {
      pill.classList.toggle('is-scroll', txt.scrollWidth > txt.clientWidth + 4);
    }
    check();
    window.addEventListener('resize', check, { passive: true });
  });

  /* pictochat send: one clean select-blip on submit */
  $$('.tii-chat form').forEach(function (f) {
    f.addEventListener('submit', function () { SFX.select(); buzz(12); });
  });

  /* ---------- image loaded state (kills shimmer) ---------- */
  $$('.tii-item__media img, .tii-ch__art img').forEach(function (img) {
    function done() { img.parentElement.classList.add('is-loaded'); }
    if (img.complete) done(); else img.addEventListener('load', done);
    img.addEventListener('error', done);
  });

  /* ============================================================
     juice — original tii interaction candy (all synth audio / tii css)
     ============================================================ */

  /* tap ripple: a soft blue ring blooms from the press point */
  var RIPPLE_SEL = '.tii-equip, .tii-status__btn, .tii-arrow, .tii-slot, .tii-footer__console, .tii-dock__btn, .tii-colour, .tii-arcade__start, .tii-ch__link, .tii-tray__btn, .tii-shortcut, .tii-bag__qty-btn, .tii-bag__remove, .tii-pager a, .tii-gallery__thumb, .tii-searchbar__go, .tii-toggle, .tii-seg__opt, .tii-stepper__btn, .tii-facet-chip, .tii-player, .tii-save--new, .tii-addr__del, .tii-ripple';
  doc.addEventListener('pointerdown', function (e) {
    var el = e.target.closest && e.target.closest(RIPPLE_SEL);
    if (!el || el.disabled) return;
    var b = el.getBoundingClientRect();
    var d = Math.max(b.width, b.height);
    var s = doc.createElement('span');
    s.className = 'tii-rp';
    s.style.width = s.style.height = d + 'px';
    s.style.left = (e.clientX - b.left - d / 2) + 'px';
    s.style.top = (e.clientY - b.top - d / 2) + 'px';
    if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
    el.appendChild(s);
    SFX.blip(523, 0.022); /* tap = the bottom rung of the buying staircase */
    s.addEventListener('animationend', function () { s.remove(); });
  }, { passive: true });

  /* bag pop — the item "becomes" a tii compo bag that spins in (kanta's
     "服がコンポ袋に変わってポロンって回る" signature on equip) */
  window.tiiBagPop = function () {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var el = doc.createElement('div');
    el.className = 'tii-bagpop';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<svg viewBox="0 0 100 112" xmlns="http://www.w3.org/2000/svg">' +
      '<defs><linearGradient id="tiiBag" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffffff"/><stop offset="1" stop-color="#DFF4FB"/></linearGradient></defs>' +
      '<path d="M34 40 V27 a16 16 0 0 1 32 0 V40" fill="none" stroke="#4FB8D8" stroke-width="4" stroke-linecap="round"/>' +
      '<path d="M20 38 h60 l-5 62 a9 9 0 0 1-9 8 H34 a9 9 0 0 1-9-8 Z" fill="url(#tiiBag)" stroke="#4FB8D8" stroke-width="4" stroke-linejoin="round"/>' +
      '<path d="M50 55 l4.6 10.4 11.4.9 -8.7 7.4 2.7 11.1 -10-6.3 -10 6.3 2.7-11.1 -8.7-7.4 11.4-.9Z" fill="#B8E8F5" stroke="#4FB8D8" stroke-width="2.4" stroke-linejoin="round"/></svg>';
    doc.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-go'); });
    setTimeout(function () { el.remove(); }, 1300);
  };

  /* achievement toast — "NEW ITEM UNLOCKED ☆" slides in from the right */
  window.tiiAchievement = function (title, sub) {
    var el = doc.createElement('div');
    el.className = 'tii-ach';
    el.setAttribute('role', 'status');
    el.innerHTML = '<span class="tii-ach__ring">★</span><span class="tii-ach__txt"><b></b><small></small></span>';
    el.querySelector('b').textContent = title || 'unlocked';
    el.querySelector('small').textContent = sub || '';
    doc.body.appendChild(el);
    SFX.equip(); buzz([20, 30, 40]);
    requestAnimationFrame(function () { el.classList.add('is-on'); });
    setTimeout(function () { el.classList.remove('is-on'); setTimeout(function () { el.remove(); }, 600); }, 3600);
  };

  /* odometer — digits roll like a coin counter */
  window.tiiOdometer = function (root, value) {
    if (!root) return;
    var min = Number(root.getAttribute('data-min') || 1);
    var digits = String(value).replace(/[^0-9]/g, '').padStart(min, '0').split('');
    root.innerHTML = '';
    /* kanta 7/30: 各桁が 0〜9 の帯を持つので、読み上げでは "0123456789" になっていた
       （バッグの個数が「ゼロイチニサン…」と読まれる）。帯は aria-hidden にして、
       実際の値だけを読ませる。見た目は一切変わらない。 */
    var sr = doc.createElement('span'); sr.className = 'visually-hidden';
    sr.textContent = String(value); root.appendChild(sr);
    digits.forEach(function (dch) {
      var col = doc.createElement('span'); col.className = 'tii-odo__col';
      col.setAttribute('aria-hidden', 'true');
      var strip = doc.createElement('span'); strip.className = 'tii-odo__strip';
      for (var n = 0; n <= 9; n++) { var sp = doc.createElement('span'); sp.textContent = n; strip.appendChild(sp); }
      col.appendChild(strip); root.appendChild(col);
      requestAnimationFrame(function () { strip.style.transform = 'translateY(-' + Number(dch) + 'em)'; });
    });
    SFX.blip(990, 0.035);
  };

  /* magnetic pull (impl 2-J, generalised): the cursor tugs the button a few
     px toward it, springs home on leave. desktop pointers only. */
  function tiiMagnetic(el, reach) {
    var rx = 0, ry = 0, tx = 0, ty = 0, raf = 0;
    var mx = reach || 14, my = Math.round((reach || 14) * 0.7);
    el.classList.add('tii-magnet');
    function loop() {
      rx += (tx - rx) * 0.18; ry += (ty - ry) * 0.18;
      el.style.setProperty('--mx', rx.toFixed(2) + 'px');
      el.style.setProperty('--my', ry.toFixed(2) + 'px');
      if (Math.abs(tx - rx) > 0.1 || Math.abs(ty - ry) > 0.1) raf = requestAnimationFrame(loop);
    }
    el.addEventListener('mousemove', function (e) {
      var b = el.getBoundingClientRect();
      tx = Math.max(-mx, Math.min(mx, (e.clientX - (b.left + b.width / 2)) * 0.3));
      ty = Math.max(-my, Math.min(my, (e.clientY - (b.top + b.height / 2)) * 0.3));
      cancelAnimationFrame(raf); loop();
    });
    el.addEventListener('mouseleave', function () { tx = ty = 0; cancelAnimationFrame(raf); loop(); });
  }
  window.tiiMagnetic = tiiMagnetic;
  if (matchMedia('(hover: hover) and (pointer: fine)').matches && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    $$('.tii-equip, .tii-look__all').forEach(function (el) { tiiMagnetic(el, 14); });
    $$('.tii-tray__btn, .tii-searchbar__go, .tii-arcade__start, [name="checkout"], [data-tii-checkout]').forEach(function (el) { tiiMagnetic(el, 8); });
  }

  /* the status-bar bag count rolls as an odometer when the cart changes */
  (function () {
    var host = $('[data-tii-bag-count]');
    if (!host) return;
    var odo = doc.createElement('span'); odo.className = 'tii-odo tii-odo--bag'; odo.setAttribute('data-min', '1');
    var prev = null;
    window.tiiBagOdo = function (n) {
      if (n === prev) return; prev = n;
      host.textContent = '';
      if (n > 0) { host.appendChild(odo); window.tiiOdometer(odo, n); }
    };
  })();


  /* typewriter — the item description types itself out in tii's voice,
     but only when scrolled into view; click to reveal the rest at once */
  (function () {
    var wrap = $('[data-tii-type-wrap]');
    if (!wrap) return;
    var src = $('[data-tii-type-source]', wrap);
    var out = $('[data-tii-type-out]', wrap);
    if (!src || !out) return;
    /* kanta 7/30: textContent ate every <br>, so "item data" typed itself out as one
       run-on line ("[ color ]navy[ size info ]one size…"). read the HTML instead and
       keep the line breaks — the panel reads as a spec sheet again, on the grid.
       also drops the invisible spacer glyphs some descriptions carry. */
    var full = (src.innerHTML || '')
      .replace(/<\s*br\s*\/?>/gi, '\n')
      .replace(/<\/\s*(p|div|li|h[1-6]|tr)\s*>/gi, '\n')
      .replace(/<[^>]*>/g, '');
    var dec = doc.createElement('textarea'); dec.innerHTML = full; full = dec.value;
    full = full
      .replace(/[​-‏⁠﻿ﭐ-﷿ﹰ-﻿]/g, '')
      .replace(/[ \t]+/g, ' ')
      .split('\n').map(function (l) { return l.trim(); })
      .join('\n').replace(/\n{3,}/g, '\n\n').trim();
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || full.length > 600) { out.textContent = full; return; }
    var started = false, i = 0, timer = 0, done = false;
    function finish() { clearInterval(timer); out.textContent = full; out.insertAdjacentHTML('beforeend', ' <span class="tii-type-cur">▼</span>'); done = true; }
    function start() {
      if (started) return; started = true;
      timer = setInterval(function () {
        if (i >= full.length) { finish(); return; }
        out.textContent += full[i++];
        if (full[i - 1] && full[i - 1].trim()) SFX.blip(1500, 0.014);
      }, 26);
    }
    wrap.addEventListener('click', function () { if (done) return; if (!started) start(); else finish(); });
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) { es.forEach(function (e) { if (e.isIntersecting) { start(); io.disconnect(); } }); }, { threshold: 0.4 });
      io.observe(wrap);
    } else { start(); }
  })();

  /* cart total rolls up as an odometer on load */
  (function () {
    var host = $('[data-tii-total-odo]');
    if (!host || !window.tiiOdometer) return;
    var raw = host.getAttribute('data-value') || '0';
    host.textContent = '';
    var wrapEl = doc.createElement('span'); wrapEl.className = 'tii-odo'; wrapEl.setAttribute('data-min', String(raw.length));
    host.appendChild(wrapEl);
    setTimeout(function () { window.tiiOdometer(wrapEl, raw); }, 120);
    var pre = doc.createElement('span'); pre.textContent = '¥'; host.insertBefore(pre, wrapEl);
  })();

  /* search submit → one clean blip */
  $$('.tii-searchbar').forEach(function (f) {
    f.addEventListener('submit', function () { SFX.select(); buzz(12); });
  });

  /* checkout — the TOP of the buying staircase (tap < add < this) */
  $$('[name="checkout"], [data-tii-checkout]').forEach(function (b) {
    b.addEventListener('click', function () { SFX.unlock(); SFX.checkout(); buzz([20, 30, 40]); });
  });

  /* gyro depth: tilt the console with the phone (android fires freely; iOS
     stays still until permission — harmless no-op). desktop uses --px/--py
     from the mouse. reduced-motion opts out via CSS. */
  if (window.DeviceOrientationEvent && matchMedia('(pointer: coarse)').matches) {
    window.addEventListener('deviceorientation', function (e) {
      if (e.gamma == null || e.beta == null) return;
      root.style.setProperty('--px', Math.max(-1, Math.min(1, e.gamma / 28)).toFixed(3));
      root.style.setProperty('--py', Math.max(-1, Math.min(1, (e.beta - 45) / 28)).toFixed(3));
    }, { passive: true });
  }

  /* ---------- showcase spin: ONE shared clock for every card (kanta D2) ----------
     the CSS sway (tii-showspin, 9s alternate = 18s full cycle) starts whenever
     an element appears — cards injected later (recs / recently played) would
     drift out of phase. so on every animationstart we pin the element to the
     document timeline: same speed, same timing, same angle. one shelf motor. */
  doc.addEventListener('animationstart', function (e) {
    if (e.animationName !== 'tii-showspin') return;
    var el = e.target;
    if (el.dataset.tiiSpinSync) return;             /* our own restart — done */
    el.dataset.tiiSpinSync = '1';
    var t = (doc.timeline && doc.timeline.currentTime) || performance.now();
    el.style.animationDelay = (-(t % 18000)).toFixed(1) + 'ms';
  }, true);

  /* ---------- tii-seg (impl 2-D): segmented control, sliding highlight ----------
     the milky highlight glides under the tapped option (DS tab memory).
     data-tii-seg names an action, data-store persists the choice. */
  var segActions = {
    view: function (v) {
      $$('.tii-inv__grid').forEach(function (g) {
        g.classList.toggle('is-list', v === 'list');
        g.classList.toggle('is-cart', v === 'cart');
      });
    }
  };
  $$('[data-tii-seg]').forEach(function (seg) {
    var opts = $$('.tii-seg__opt', seg);
    if (!opts.length) return;
    var hl = doc.createElement('span');
    hl.className = 'tii-seg__hl';
    hl.setAttribute('aria-hidden', 'true');
    seg.insertBefore(hl, seg.firstChild);
    function place(el, instant) {
      if (instant) hl.style.transition = 'none';
      hl.style.width = el.offsetWidth + 'px';
      hl.style.transform = 'translateX(' + el.offsetLeft + 'px)';
      if (instant) { void hl.offsetWidth; hl.style.transition = ''; }
    }
    function select(el, silent) {
      opts.forEach(function (o) {
        o.classList.toggle('is-active', o === el);
        o.setAttribute('aria-pressed', o === el ? 'true' : 'false');
      });
      place(el, silent);
      if (!silent) { SFX.tick(); buzz(8); }
      var act = segActions[seg.getAttribute('data-tii-seg')];
      if (act) act(el.getAttribute('data-value'));
      var key = seg.getAttribute('data-store');
      if (key && !silent) store(key, el.getAttribute('data-value'));
    }
    opts.forEach(function (o, i) {
      o.addEventListener('click', function () { if (!o.classList.contains('is-active')) select(o); });
      o.addEventListener('keydown', function (e) {
        var n = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : null;
        if (n === null) return;
        e.preventDefault();
        n = (n + opts.length) % opts.length;
        opts[n].focus(); select(opts[n]);
      });
    });
    window.addEventListener('resize', function () {
      var a = seg.querySelector('.tii-seg__opt.is-active');
      if (a) place(a, true);
    }, { passive: true });
    var init = seg.querySelector('.tii-seg__opt.is-active') || opts[0];
    var key = seg.getAttribute('data-store');
    if (key) {
      var sv = store(key);
      var m = opts.filter(function (o) { return o.getAttribute('data-value') === sv; })[0];
      if (m) init = m;
    }
    select(init, true);
  });

  /* ---------- tii-radio / tii-check sounds (impl 2-B / 2-C) ----------
     native inputs stay in charge (a11y for free); tii adds the feel. */
  doc.addEventListener('change', function (e) {
    if (e.target.matches && e.target.matches('.tii-radio input')) { SFX.select(); buzz(10); }
    else if (e.target.matches && e.target.matches('.tii-checkrow input')) { SFX.tick(); buzz(8); }
  });

  /* ---------- customers: säve-data screens (impl 1-A) ---------- */
  (function () {
    /* any tii <details> ticks when it folds open/closed */
    $$('details.tii-details').forEach(function (d) {
      d.addEventListener('toggle', function () { SFX.tick(); });
    });
    /* login: unfold the recover panel on #recover or after a failed try */
    var rec = $('[data-tii-recover]');
    if (rec && (location.hash === '#recover' || rec.querySelector('.tii-form__err'))) {
      rec.setAttribute('open', '');
    }
    /* account: surface the local tii kid No. onto the live säve card —
       two layers (device card / cloud account), one save file */
    var kidEl = $('[data-tii-kid-no]');
    if (kidEl) {
      try {
        var kid = JSON.parse(localStorage.getItem('tii:kid') || 'null');
        if (kid && kid.no) {
          kidEl.textContent = '#' + kid.no;
          kidEl.parentElement.hidden = false;
        }
      } catch (e) {}
    }
    /* addresses: country → prefecture/province pairing (Shopify puts the
       provinces on each country option as data-provinces JSON) */
    $$('[data-tii-country]').forEach(function (sel) {
      var wrap = doc.getElementById(sel.getAttribute('data-province-wrap'));
      var prov = wrap && wrap.querySelector('select');
      var def = sel.getAttribute('data-default');
      if (def) sel.value = def;
      function paint() {
        if (!prov || !wrap) return;
        var opt = sel.options[sel.selectedIndex];
        var provinces = [];
        try { provinces = JSON.parse((opt && opt.getAttribute('data-provinces')) || '[]'); } catch (e) {}
        prov.innerHTML = '';
        provinces.forEach(function (p) {
          var o = doc.createElement('option');
          o.value = p[0]; o.textContent = p[1];
          prov.appendChild(o);
        });
        var pd = prov.getAttribute('data-default');
        if (pd) prov.value = pd;
        wrap.hidden = provinces.length === 0;
      }
      sel.addEventListener('change', function () { paint(); SFX.tick(); });
      paint();
    });
    /* addresses: edit unfolds in place */
    $$('[data-tii-addr-edit]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var tgt = doc.getElementById(btn.getAttribute('data-tii-addr-edit'));
        if (!tgt) return;
        var open = tgt.hidden;
        tgt.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
        if (open) { SFX.select(); } else { SFX.back(); }
      });
    });
    /* erase = two taps, DS style — the button itself asks "sure?" (no
       browser popup breaking the world). arms for 3.2s, then relaxes. */
    $$('[data-tii-addr-del]').forEach(function (btn) {
      var armed = false, t;
      btn.addEventListener('click', function (e) {
        if (!armed) {
          e.preventDefault();
          armed = true;
          btn.classList.add('is-armed');
          btn.textContent = 'sure? tap again';
          SFX.error(); buzz(20);
          t = setTimeout(function () {
            armed = false;
            btn.classList.remove('is-armed');
            btn.textContent = 'erase..';
          }, 3200);
        } else {
          clearTimeout(t);
          SFX.back(); /* second tap submits the delete form naturally */
        }
      });
    });
  })();

  /* ---------- equip set (impl 1-C): one press equips the whole löok ----------
     rows are plain <form>s (JS-off buying stays alive); this layer adds the
     checked-total, the bulk /cart/add.js and the full-set fanfare. */
  $$('[data-tii-look]').forEach(function (sec) {
    var rows = $$('[data-tii-look-row]', sec);
    if (!rows.length) return;
    var allBtn = $('[data-tii-look-all]', sec);
    var allLabel = $('[data-tii-look-all-label]', sec);
    var totalEl = $('[data-tii-look-total]', sec);

    function rowState(row) {
      var idEl = row.querySelector('[data-tii-look-id]');
      if (!idEl) return null;
      var opt = idEl.tagName === 'SELECT' ? idEl.options[idEl.selectedIndex] : idEl;
      var pick = row.querySelector('[data-tii-look-pick]');
      return {
        id: Number(idEl.value),
        price: Number((opt && opt.getAttribute('data-price')) || 0),
        available: !!opt && opt.getAttribute('data-available') === 'true',
        picked: !!(pick && pick.checked)
      };
    }
    function paint() {
      var picked = [], total = 0, buyable = 0;
      rows.forEach(function (row) {
        var s = rowState(row);
        if (!s) return;
        var priceEl = row.querySelector('[data-tii-look-price]');
        if (priceEl && s.price) priceEl.textContent = money(s.price);
        if (s.available) buyable++;
        if (s.picked && s.available) { picked.push(s); total += s.price; }
      });
      if (totalEl) totalEl.textContent = picked.length ? money(total) : '—';
      if (allBtn) {
        if (!buyable) { allBtn.disabled = true; allLabel.textContent = allBtn.getAttribute('data-label-soldout'); }
        else if (!picked.length) { allBtn.disabled = true; allLabel.textContent = allBtn.getAttribute('data-label-none'); }
        else {
          allBtn.disabled = false;
          var base = picked.length === buyable ? allBtn.getAttribute('data-label-all') : allBtn.getAttribute('data-label-some');
          allLabel.textContent = base.replace('Ⓐ ', '') + ' ×' + picked.length;
        }
      }
      return picked;
    }
    function addItems(items, btn, n) {
      if (btn) btn.disabled = true;
      if (btn === allBtn && allLabel) allLabel.textContent = allBtn.getAttribute('data-label-adding');
      fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) throw new Error('equip failed');
        return r.json();
      }).then(function () {
        SFX.insert(); buzz([20, 40, 20]);
        if (window.tiiBagPop) window.tiiBagPop();
        if (n > 1) {
          sec.classList.add('is-equipped');
          if (allLabel) allLabel.textContent = allBtn.getAttribute('data-label-done');
          if (window.tiiAchievement) window.tiiAchievement('FULL SET EQUIPPED ☆', n + ' items :: added to bag');
        } else if (window.tiiAchievement) {
          window.tiiAchievement('NEW ITEM UNLOCKED ☆', 'added to bag');
        }
        refreshCart();
        setTimeout(function () { if (window.tiiOpenDrawer) window.tiiOpenDrawer(); }, 950);
        setTimeout(function () { if (btn) btn.disabled = false; sec.classList.remove('is-equipped'); paint(); }, 2200);
      }).catch(function () {
        SFX.error(); buzz(30);
        toast('cöuld nöt equip :: try again');
        if (btn) btn.disabled = false;
        paint();
      });
    }
    if (allBtn) {
      allBtn.hidden = false; /* JS-only upgrade: appears once wired */
      allBtn.addEventListener('click', function () {
        var picked = paint();
        if (!picked.length) return;
        SFX.step(0);
        addItems(picked.map(function (s) { return { id: s.id, quantity: 1 }; }), allBtn, picked.length);
      });
    }
    rows.forEach(function (row) {
      row.addEventListener('submit', function (e) {
        if (!window.fetch) return; /* no fetch → plain POST to /cart/add */
        e.preventDefault();
        var s = rowState(row);
        if (!s || !s.available) { SFX.error(); return; }
        addItems([{ id: s.id, quantity: 1 }], row.querySelector('[data-tii-look-one]'), 1);
      });
    });
    sec.addEventListener('change', function (e) {
      if (e.target.matches('.tii-look__size')) { SFX.tick(); buzz(6); }
      paint(); /* tii-checkrow inputs already blip via the global handler */
    });
    paint();
  });

  /* ---------- tiö's word (impl 2-N): RPG dialog box on the item screen ----------
     types itself when scrolled into view, one soft letter-blip per other
     character, ▼ bobs when done. tap the box to reveal at once. */
  $$('[data-tii-dlg]').forEach(function (dlg) {
    var srcEl = $('[data-tii-dlg-src]', dlg);
    var out = $('[data-tii-dlg-out]', dlg);
    var cur = $('[data-tii-dlg-cur]', dlg);
    if (!out) return;
    var full = ((srcEl && srcEl.textContent) || out.textContent).replace(/\s+/g, ' ').trim();
    function settle() { out.textContent = full; if (cur) cur.hidden = false; dlg.classList.remove('is-talking'); }
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window) || !full) { settle(); return; }
    out.textContent = '';
    var started = false, done = false, i = 0, timer = 0;
    function finish() { if (done) return; done = true; clearInterval(timer); settle(); }
    function start() {
      if (started) return; started = true;
      dlg.classList.add('is-talking');
      timer = setInterval(function () {
        if (i >= full.length) { finish(); return; }
        out.textContent += full[i++];
        if (full[i - 1] && full[i - 1].trim() && i % 2) SFX.blip(1560, 0.011);
      }, 34);
    }
    dlg.addEventListener('click', function () { if (!started) start(); else finish(); });
    dlg.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      if (!started) start(); else finish();
    });
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (en) { if (en.isIntersecting) { start(); io.disconnect(); } });
    }, { threshold: 0.5 });
    io.observe(dlg);
  });

  /* ---------- haptic snap (impl 2-K): carousels click into place ----------
     native scroll-snap does the physics; tii adds the buzz + a glow pulse on
     the card that just seated. uses onscrollsnapchange where the browser has
     it, a scroll-settle fallback everywhere else. */
  $$('[data-tii-snap]').forEach(function (row) {
    function seat(el) {
      if (!el || el.nodeType !== 1) return;
      SFX.hov(); buzz(8);
      el.classList.remove('is-snapped'); void el.offsetWidth;
      el.classList.add('is-snapped');
      setTimeout(function () { el.classList.remove('is-snapped'); }, 620);
    }
    if ('onscrollsnapchange' in row) {
      row.addEventListener('scrollsnapchange', function (e) { seat(e.snapTargetInline || e.snapTargetBlock); });
      return;
    }
    var last = null, t;
    function nearest() {
      var r = row.getBoundingClientRect(), best = null, bd = 1e9;
      Array.prototype.forEach.call(row.children, function (c) {
        var d = Math.abs(c.getBoundingClientRect().left - r.left);
        if (d < bd) { bd = d; best = c; }
      });
      return best;
    }
    last = nearest(); /* don't fire on load */
    row.addEventListener('scroll', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var el = nearest();
        if (el && el !== last) { last = el; seat(el); }
      }, 110);
    }, { passive: true });
  });

  /* ---------- controller feel: d-pad the channel grid, Enter launches ---------- */
  $$('.tii-wii__grid').forEach(function (grid) {
    var links = $$('.tii-ch__link', grid);
    if (!links.length) return;
    grid.addEventListener('keydown', function (e) {
      var i = links.indexOf(doc.activeElement);
      if (i < 0) return;
      var top0 = links[0].getBoundingClientRect().top;
      var cols = links.filter(function (l) { return Math.abs(l.getBoundingClientRect().top - top0) < 4; }).length || 1;
      var n = i;
      if (e.key === 'ArrowRight') n = i + 1;
      else if (e.key === 'ArrowLeft') n = i - 1;
      else if (e.key === 'ArrowDown') n = i + cols;
      else if (e.key === 'ArrowUp') n = i - cols;
      else return;
      e.preventDefault();
      if (n < 0 || n >= links.length) return;
      links[n].focus(); SFX.tick(); buzz(4);
    });
  });
})();

/* ===== tii v1.8 (2026-07-17 night) — päss cöde: discount code via /discount/CODE cookie.
   progressive enhancement: without JS the row is inert, checkout stays plain. ===== */
(function () {
  var box = document.querySelector('[data-tii-code]');
  if (!box) return;
  var input = box.querySelector('[data-tii-code-in]');
  var btn = box.querySelector('[data-tii-code-btn]');
  var msg = box.querySelector('[data-tii-code-msg]');
  var SFX = window.tiiSFX;
  var saved = null;
  try { saved = localStorage.getItem('tii:code'); } catch (e) {}
  if (saved) { input.value = saved; msg.textContent = 'cöde in slöt :: ' + saved; box.classList.add('is-ok'); }

  function apply() {
    var code = (input.value || '').trim().toUpperCase();
    box.classList.remove('is-ok', 'is-err');
    if (!code) {
      box.classList.add('is-err');
      msg.textContent = 'nö cöde..';
      if (SFX && !SFX.muted()) SFX.error();
      if (navigator.vibrate) navigator.vibrate(8);
      return;
    }
    fetch('/discount/' + encodeURIComponent(code), { redirect: 'manual' })
      .catch(function () {})
      .then(function () {
        try { localStorage.setItem('tii:code', code); } catch (e) {}
        box.classList.add('is-ok');
        msg.textContent = 'cöde equipped ☆ (checked at checköut)';
        if (SFX && !SFX.muted()) SFX.secret();
        if (navigator.vibrate) navigator.vibrate(8);
      });
  }
  btn.addEventListener('click', apply);
  input.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') { e.preventDefault(); apply(); }
    else if (e.key.length === 1 && SFX && !SFX.muted()) SFX.blip();
  });
})();

/* v1.8 — DS software keyboard on the search page. builds itself only when JS
   is alive; typing dispatches 'input' so predictive search reacts live. */
(function () {
  var mount = document.querySelector('[data-tii-kbd]');
  if (!mount) return;
  var input = document.querySelector('[data-tii-predictive-input]');
  var form = input && input.closest('form');
  if (!input || !form) return;
  var SFX = window.tiiSFX;
  var rows = ['1234567890', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'];
  rows.forEach(function (r) {
    var div = document.createElement('div');
    div.className = 'tii-kbd__row';
    r.split('').forEach(function (ch) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'tii-kbd__key'; b.textContent = ch;
      div.appendChild(b);
    });
    mount.appendChild(div);
  });
  var last = document.createElement('div');
  last.className = 'tii-kbd__row';
  [['späce', 'wide', ' '], ['⌫', '', 'BS'], ['Ⓐ go', 'go', 'GO']].forEach(function (k) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'tii-kbd__key' + (k[1] ? ' tii-kbd__key--' + k[1] : '');
    b.textContent = k[0]; b.dataset.k = k[2];
    last.appendChild(b);
  });
  mount.appendChild(last);
  mount.hidden = false;
  mount.setAttribute('role', 'group');
  mount.setAttribute('aria-label', 'on-screen keyboard');
  mount.addEventListener('pointerdown', function (e) { e.preventDefault(); }); /* 入力欄からフォーカスを奪わない=スマホでnative鍵盤が暴発しない */
  mount.addEventListener('click', function (e) {
    var b = e.target.closest('.tii-kbd__key');
    if (!b) return;
    var k = b.dataset.k || b.textContent;
    if (k === 'GO') {
      if (SFX && !SFX.muted()) SFX.select();
      form.requestSubmit ? form.requestSubmit() : form.submit();
      return;
    }
    if (k === 'BS') input.value = input.value.slice(0, -1);
    else input.value += k;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    if (SFX && !SFX.muted()) SFX.blip();
    if (navigator.vibrate) navigator.vibrate(4);
  });
})();

/* ---------- share: copy link (A-1) — progressive enhance the copy button ---------- */
(function () {
  var doc = document;
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-tii-share-copy]');
    if (!btn) return;
    var url = btn.getAttribute('data-url') || location.href;
    var label = btn.querySelector('[data-tii-share-copy-label]') || btn;
    var done = function () {
      var prev = label.textContent;
      label.textContent = 'copied ☆';
      btn.classList.add('is-copied');
      setTimeout(function () { label.textContent = prev; btn.classList.remove('is-copied'); }, 1600);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done, function () { window.prompt('copy link:', url); });
    } else {
      window.prompt('copy link:', url);
    }
  });
})();

/* ---------- cartridge rack (232938): shoulder buttons + lit slot pips ---------- */
(function () {
  var racks = document.querySelectorAll('[data-tii-rack]');
  if (!racks.length) return;
  racks.forEach(function (rack) {
    var row = rack.querySelector('[data-tii-rack-row]');
    if (!row) return;
    var slots = Array.prototype.slice.call(row.querySelectorAll('[data-tii-rack-slot]'));
    var prev = rack.querySelector('[data-tii-rack-prev]');
    var next = rack.querySelector('[data-tii-rack-next]');
    var pipWrap = rack.querySelector('[data-tii-rack-pips]');
    if (!slots.length) return;

    /* build one pip per cartridge */
    var pips = [];
    if (pipWrap) {
      slots.forEach(function () {
        var p = document.createElement('span');
        p.className = 'tii-rack__pip';
        pipWrap.appendChild(p); pips.push(p);
      });
    }

    var overflow = function () { return row.scrollWidth - row.clientWidth > 4; };
    var showShoulders = function () {
      var on = overflow();
      if (prev) prev.hidden = !on;
      if (next) next.hidden = !on;
    };
    showShoulders();
    window.addEventListener('resize', showShoulders);

    var step = function () {
      var s = slots[0];
      return s ? (s.getBoundingClientRect().width + 14) : row.clientWidth * 0.8;
    };
    if (prev) prev.addEventListener('click', function () { row.scrollBy({ left: -step() * 2, behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { row.scrollBy({ left: step() * 2, behavior: 'smooth' }); });

    var raf = 0;
    var litIndex = -1;
    var sync = function () {
      raf = 0;
      var mid = row.getBoundingClientRect().left + row.clientWidth / 2;
      var best = 0, bestDist = Infinity;
      slots.forEach(function (s, i) {
        var b = s.getBoundingClientRect();
        var d = Math.abs((b.left + b.width / 2) - mid);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      if (best !== litIndex) {
        var wasInitial = litIndex === -1;
        litIndex = best;
        pips.forEach(function (p, i) { p.classList.toggle('is-lit', i === best); });
        /* DSi behaviour (kanta 7/20): centred cartridge boings + title bubble */
        slots.forEach(function (s, i) {
          s.classList.toggle('is-front', i === best);
          if (i === best) { s.classList.remove('is-boing'); void s.offsetWidth; s.classList.add('is-boing'); }
        });
        var bub = rack.querySelector('[data-tii-rack-bubble]');
        if (bub) {
          var link = slots[best].querySelector('a');
          var title = link ? (link.getAttribute('aria-label') || '') : '';
          var txt = bub.querySelector('[data-tii-rack-bubble-txt]');
          if (txt) txt.textContent = title;
          var sb = slots[best].getBoundingClientRect();
          var stb = rack.querySelector('.tii-rack__stage').getBoundingClientRect();
          bub.style.left = (sb.left - stb.left + sb.width / 2) + 'px';
          bub.hidden = !title;
          bub.classList.remove('is-pop'); void bub.offsetWidth; bub.classList.add('is-pop');
        }
        /* kanta: "音でも楽しませる・スライドの感じ" — a soft tick as each cart centres */
        if (!wasInitial && window.tiiSFX && (!window.tiiSFX.muted || !window.tiiSFX.muted())) {
          window.tiiSFX.blip(620, 0.028, 'triangle');
          if (navigator.vibrate) navigator.vibrate(3);
        }
      }
    };
    row.addEventListener('scroll', function () { if (!raf) raf = requestAnimationFrame(sync); }, { passive: true });
    sync();
  });
})();

/* ---------- spin swap (kanta 7/30): the still shows first, the turn arrives after ----------
   measured: Shopify's CDN hands iPhone/Safari a 2.2MB GIF for our animated webp
   (Chrome gets the 519KB webp) — one image was 66% of a 3.26MB mobile product page.
   so the <img> ships with the still, and the spin is fetched OFF the critical path
   and swapped in only once it's fully decoded (no flash, no half-drawn frame).
   respects reduced-motion, Data Saver and 2g — those simply keep the still. */
(function () {
  var imgs = document.querySelectorAll('img[data-tii-spin]');
  if (!imgs.length) return;
  var c = navigator.connection || {};
  var thrifty = c.saveData === true || /(^|-)2g$/.test(c.effectiveType || '');
  if (matchMedia('(prefers-reduced-motion: reduce)').matches || thrifty) return;
  var start = function () {
    imgs.forEach(function (img) {
      var url = img.getAttribute('data-tii-spin');
      if (!url) return;
      var pre = new Image();
      pre.decoding = 'async';
      pre.onload = function () {
        var put = function () {
          /* the customer already flicked to another photo — never yank it back */
          if (img.getAttribute('data-tii-spin') !== url) return;
          img.src = url; img.removeAttribute('data-tii-spin');
        };
        if (pre.decode) pre.decode().then(put, put); else put();
      };
      pre.src = url;
    });
  };
  /* wait for the page to finish, then one idle beat — the spin never competes with LCP */
  var arm = function () { 'requestIdleCallback' in window ? requestIdleCallback(start, { timeout: 2500 }) : setTimeout(start, 700); };
  document.readyState === 'complete' ? arm() : addEventListener('load', arm);
})();

/* ---------- rotation media: nudge muted autoplay (kanta 7/18, happy99-style spin) ---------- */
(function () {
  var vids = document.querySelectorAll('video.tii-gallery__vid'); /* animated-webp spins are <img>, never touched here */
  if (!vids.length) return;
  var RM = matchMedia('(prefers-reduced-motion: reduce)').matches;
  vids.forEach(function (v) {
    v.muted = true; v.setAttribute('playsinline', ''); v.loop = true;
    v.playbackRate = 0.5; /* premium slow turntable (research: slow + constant reads high-end) */
    if (RM) return; /* reduced-motion: leave paused on the poster */
    var go = function () { v.playbackRate = 0.5; var p = v.play(); if (p && p.catch) p.catch(function () {}); };
    go();
    /* start the spin the instant enough is buffered — kill the frozen lag */
    ['loadeddata', 'canplay', 'canplaythrough'].forEach(function (ev) { v.addEventListener(ev, go); });
    /* some mobile browsers only allow play after first touch — arm a one-shot */
    var once = function () { go(); document.removeEventListener('touchstart', once); document.removeEventListener('click', once); };
    document.addEventListener('touchstart', once, { passive: true, once: true });
    document.addEventListener('click', once, { once: true });
    /* Wii "grab and look": hover pauses the spin on desktop; tap toggles on touch */
    v.addEventListener('mouseenter', function () { v.pause(); });
    v.addEventListener('mouseleave', function () { go(); });
    v.addEventListener('click', function (e) { e.preventDefault(); if (v.paused) go(); else v.pause(); });
  });
})();

/* ---------- audio unlock safety (kanta 7/20): the boot tap used to be the only
   unlock — returning visitors (boot shows once a day) scrolled straight to the
   typewriter and heard nothing. first gesture anywhere now unlocks the console. */
(function () {
  /* ACO port R43/wake: retry on EVERY gesture, never unbind (one-shot flags are the
     iOS silence bug). also resume when the tab/screen comes back. */
  function wake() {
    try { if (window.tiiSFX && window.tiiSFX.unlock) window.tiiSFX.unlock(); } catch (e) {}
  }
  ['pointerdown', 'touchstart', 'touchend', 'click', 'keydown'].forEach(function (ev) {
    document.addEventListener(ev, wake, { passive: true });
  });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) wake(); });
})();

/* ---------- native share (ACO port #7): the OS share sheet is the only reliable
   path on phones (pinterest deep-links get hijacked by the app). ---------- */
(function () {
  var btn = document.querySelector('[data-tii-share-native]');
  if (!btn || !navigator.share) return;
  btn.hidden = false;
  btn.addEventListener('click', function () {
    try {
      navigator.share({ title: btn.getAttribute('data-title') || document.title, url: btn.getAttribute('data-url') || location.href }).catch(function () {});
    } catch (e) {}
  });
})();
